import React, { useMemo, useState, useEffect, Suspense} from 'react';
import { Outlet, useNavigate, useLocation} from 'react-router-dom';
import { CopilotPanel, AmbientCopilotRail } from './copilotRemotes';
import {
 Button,
 Sidebar,
 SkipLink,
 Toaster,
 Sheet,
 SheetContent,
 SheetTitle,
 Input,
 type MenuItem,
 type SidebarSection,
} from '@tyndo/ui-kit';
import MobileBottomNav, { type BottomNavItem } from './MobileBottomNav';
import { useTranslation } from 'react-i18next';
import { initUiPreferences } from './uiPreferences';
import TopBar from './TopBar';
import SeverityStripe from './SeverityStripe';
import CommandPalette from './CommandPalette';
import { useAuthRoles } from '../auth/useSyncAuthRoles';
import { menuRegistry, viewRegistry, actionRegistry, moduleManifestRegistry, type MenuTreeNode} from '../registries';
import type { UiMenuItem} from '@tyndo/types';
import { viewSlugForUrl, resolveSlugToViewId } from '../utils/urlHelpers';
import {
 inferIsaGroup,
 ISA_GROUP_LABEL,
 ISA_GROUP_ORDER,
 type IsaGroup,
} from './isaGroups';
import { icons, type LucideIcon} from 'lucide-react';
import { Circle, Boxes, Factory, BellRing, Wrench, Home, AlertTriangle, RefreshCw, X as XIcon, Loader2, Menu as MenuIcon, Search} from 'lucide-react';
import tyndoMark from '../assets/tyndo-mark.png';
import { NavModeProvider, useNavMode } from './NavModeContext';
import { ModuleAccessProvider } from './ModuleAccessContext';
import { collapseSingletonGroups } from './collapseSingletonGroups';
import { useShellModuleAccess } from './useShellModuleAccess';
import { remoteHealth, type RemoteHealth } from '../observability/remoteHealth';
import { useTenantContext } from '@tyndo/hooks';
import { useAdaptiveShell } from '../adaptive/useAdaptiveShell';
import { adaptSidebarSections, moduleKeyForMenuItem } from '../adaptive/adaptiveNav';
import { DegradedModuleBadge, REMOTE_STATUS_LABEL } from './FederationHealthBadge';
import {
 filterSidebarSections,
 toggleId,
 collapsedGroupsKeyFor,
 readCollapsedGroups,
 writeCollapsedGroups,
} from './sidebarSearch';

/**
 * Subscribe to the per-remote federation health registry. Returns the live
 * list of degraded remotes so the shell can badge their nav entries and render
 * a recovery banner. Uses useSyncExternalStore so a markFailed/markOk/retry
 * from anywhere re-renders the chrome.
 */
function useDegradedRemotes(): RemoteHealth[] {
 const [snapshot, setSnapshot] = useState<RemoteHealth[]>(() => remoteHealth.degraded());
 useEffect(() => {
 const update = () => setSnapshot(remoteHealth.degraded());
 update();
 return remoteHealth.subscribe(update);
 }, []);
 return snapshot;
}

// Versioned + tenant-scoped sidebar collapse key. The version prefix lets us
// change the stored shape without colliding with a stale value, and scoping by
// tenantId stops one tenant's collapse preference leaking into another's
// session on a shared workstation. A null tenant (pre-context) falls back to a
// shared bucket so the very first render still restores something sane.
const SIDEBAR_KEY_PREFIX ='tyndo.sidebar.v2.collapsed';
const LEGACY_SIDEBAR_KEY ='tyndo.sidebar.collapsed';
const sidebarKeyFor = (tenantId: string | null | undefined): string =>
 `${SIDEBAR_KEY_PREFIX}.${tenantId ?? 'shared'}`;

function resolveIcon(name?: string): LucideIcon | undefined {
 if (!name) return undefined;
 return (icons as Record<string, LucideIcon>)[name];
}

/**
 * Resolve an icon for a menu node, falling back through:
 *  1. The menu node's own `icon` string (if it matches a Lucide name)
 *  2. The owning module's manifest icon
 *  3. A neutral `Circle` so the row never renders icon-less
 */
function resolveMenuIcon(item: UiMenuItem, moduleId?: string): LucideIcon {
 const direct = resolveIcon(item.icon);
 if (direct) return direct;
 if (moduleId) {
 const mod = moduleManifestRegistry.get(moduleId);
 const fromMod = resolveIcon(mod?.icon);
 if (fromMod) return fromMod;
 }
 return Circle;
}

function toSidebarItems(nodes: MenuTreeNode[], degradedByModuleId?: Map<string, RemoteHealth>): MenuItem[] {
 const mapped = nodes.map((node) => {
 const item = node.item;
 const moduleId = node.moduleId;
 // Surface a degraded indicator when the owning module's federated bundle
 // failed to load (404/CORS/hung dev server/version skew). The badge sits
 // in the Sidebar primitive's trailing slot so an operator sees a red
 // marker on Production/SCADA rather than discovering the failure lazily —
 // and it opens an error popover with a retry affordance.
 const degraded = moduleId ? degradedByModuleId?.get(moduleId) : undefined;
 return {
 id: item.menuId ?? item.id ?? '',
 label: item.label ?? '',
 icon: resolveMenuIcon(item, moduleId),
 group: item.group as string | undefined,
 badge: degraded ? (
 <DegradedModuleBadge health={degraded} label={item.label ?? moduleId ?? ''} />
 ) : undefined,
 children: node.children?.length ? toSidebarItems(node.children, degradedByModuleId) : undefined,
};
});
  return collapseSingletonGroups(mapped);
}

/** Bucket top-level menu items into ISA-95 sections, preserving order. */
function bucketIntoSections(
 nodes: MenuTreeNode[],
 flatItems: MenuItem[],
 opts?: { mode?: 'isa95' | 'module' | 'persona' | 'industry'; visibleModules?: Set<string> | null }
): SidebarSection[] {
 const mode = opts?.mode ?? 'isa95';
 const visibleModules = opts?.visibleModules ?? null;

 // Pair items with their owning moduleId for filtering and grouping.
 const paired = flatItems.map((item, idx) => {
   const node = nodes[idx];
   const moduleId: string | undefined = node?.moduleId;
   return { item, moduleId, explicit: node?.item?.group ?? item.group };
 });

 // Persona / industry filter — drop modules outside the visible set.
 const filtered = visibleModules
   ? paired.filter((p) => !p.moduleId || visibleModules.has(p.moduleId))
   : paired;

 // Mode: module — one section per moduleId, in alphabetical order.
 if (mode === 'module') {
   const byModule = new Map<string, MenuItem[]>();
   for (const p of filtered) {
     const key = p.moduleId ?? 'other';
     if (!byModule.has(key)) byModule.set(key, []);
     byModule.get(key)!.push(p.item);
   }
   return [...byModule.entries()]
     .sort(([a], [b]) => a.localeCompare(b))
     .map(([id, items]) => ({ id, label: id, items }));
 }

 // Mode: isa95 / persona / industry — bucket by ISA-95 layer.
 const buckets: Record<IsaGroup, MenuItem[]> = {
 operate: [],
 plan: [],
 commerce: [],
 people: [],
 admin: [],
};

 filtered.forEach(({ item, moduleId, explicit }) => {
 // Prefer an explicit UiMenuItem.group; otherwise infer from the owning
 // module's manifest metadata (isa95Tier / productFamily) so plant-floor
 // modules bucket into their real operate/plan group instead of Admin.
 const meta = moduleId ? moduleManifestRegistry.get(moduleId) : undefined;
 const group = (explicit as IsaGroup) ?? inferIsaGroup(moduleId, meta);
 if (buckets[group]) {
 buckets[group].push(item);
} else {
 buckets.admin.push(item);
}
});

 return ISA_GROUP_ORDER.map((g) => ({
 id: g,
 label: ISA_GROUP_LABEL[g],
 items: buckets[g],
}));
}

export default function AppShell() {
 return (
   <NavModeProvider>
     <AppShellInner />
   </NavModeProvider>
 );
}

function AppShellInner() {
 const navigate = useNavigate();
 const location = useLocation();
 const { t } = useTranslation();
 const { tenantId } = useTenantContext();
 const sidebarKey = sidebarKeyFor(tenantId);

 const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => {
 if (typeof window ==='undefined') return false;
 try {
 const scoped = window.localStorage.getItem(sidebarKeyFor(tenantId));
 if (scoped !== null) return scoped ==='1';
 // One-time migration from the unversioned, unscoped key.
 return window.localStorage.getItem(LEGACY_SIDEBAR_KEY) ==='1';
} catch {
 return false;
}
});
 const [activeMenuId, setActiveMenuId] = useState<string | undefined>(undefined);
 const [copilotOpen, setCopilotOpen] = useState(false);
 const [paletteOpen, setPaletteOpen] = useState(false);
 // Sidebar nav filter — a live text query that narrows modules/views by name.
 // While a query is active every group/child renders expanded so matches are
 // never hidden behind collapse state.
 const [navQuery, setNavQuery] = useState('');
 // Persisted per-group (ISA section / module bucket) collapse, tenant-scoped
 // like the rail-collapse key so preferences never leak across tenants.
 const collapsedGroupsKey = collapsedGroupsKeyFor(tenantId);
 const [collapsedGroups, setCollapsedGroups] = useState<string[]>(() =>
 readCollapsedGroups(collapsedGroupsKeyFor(tenantId))
 );
 useEffect(() => {
 // Tenant switch mid-session — load that tenant's own group collapse.
 setCollapsedGroups(readCollapsedGroups(collapsedGroupsKey));
 }, [collapsedGroupsKey]);
 const handleSectionToggle = (sectionId: string) => {
 setCollapsedGroups((prev) => {
 const next = toggleId(prev, sectionId);
 writeCollapsedGroups(collapsedGroupsKey, next);
 return next;
 });
 };
 // Mobile drawer state — when the viewport is < md (768px), the sidebar
 // collapses behind a hamburger and slides over the content as an overlay.
 // This is independent of `sidebarCollapsed` which controls the desktop
 // mini-icon mode. Default closed on every mount so a small-screen user
 // sees the full content area first.
 const [mobileNavOpen, setMobileNavOpen] = useState(false);

 // The mobile nav renders as a portal Sheet whose scrim isn't governed by the
 // panel's `md:hidden`; when the viewport grows past md (rotation, window
 // resize) close it so the desktop column takes over with no orphaned scrim
 // or scroll lock.
 useEffect(() => {
 if (typeof window.matchMedia !== 'function') return;
 const mq = window.matchMedia('(min-width: 768px)');
 const onChange = (e: MediaQueryListEvent) => {
 if (e.matches) setMobileNavOpen(false);
};
 mq.addEventListener('change', onChange);
 return () => mq.removeEventListener('change', onChange);
}, []);

 // Persist sidebar collapse under the versioned, tenant-scoped key.
 useEffect(() => {
 try {
 window.localStorage.setItem(sidebarKey, sidebarCollapsed ? '1' : '0');
} catch {
 /* ignore */
}
}, [sidebarCollapsed, sidebarKey]);

 // When the tenant changes mid-session (e.g. tenant switch on a shared
 // workstation), reload that tenant's own collapse preference so one tenant's
 // choice never bleeds into another's.
 useEffect(() => {
 try {
 const scoped = window.localStorage.getItem(sidebarKey);
 if (scoped !== null) setSidebarCollapsed(scoped ==='1');
} catch {
 /* ignore */
}
 // Only react to tenant (key) changes, not local toggles.
}, [sidebarKey]);

 // One-time migration of the legacy, unversioned/unscoped sidebar collapse key.
 // The init reader above only *reads* the legacy value as a fallback; without
 // this the legacy key lingers forever and a future shape change to v2 can't
 // safely assume the v1 key is gone. Read v1, fold it into the active scoped v2
 // key when v2 hasn't been written yet, then delete v1 so the migration runs
 // exactly once per browser.
 useEffect(() => {
 try {
 const legacy = window.localStorage.getItem(LEGACY_SIDEBAR_KEY);
 if (legacy === null) return;
 if (window.localStorage.getItem(sidebarKey) === null) {
 window.localStorage.setItem(sidebarKey, legacy === '1' ? '1' : '0');
}
 window.localStorage.removeItem(LEGACY_SIDEBAR_KEY);
} catch {
 /* ignore — storage may be unavailable (private mode / blocked) */
}
}, [sidebarKey]);

 // Restore theme + density preferences and start cross-tab sync
 // (BroadcastChannel with storage-event fallback). Idempotent.
 useEffect(() => {
 initUiPreferences();
}, []);

 // Cmd+K → Command Palette. Cmd+J → Copilot.
 useEffect(() => {
 const handleKeyDown = (e: KeyboardEvent) => {
 const meta = e.ctrlKey || e.metaKey;
 if (meta && e.key.toLowerCase() ==='k') {
 e.preventDefault();
 setPaletteOpen((prev) => !prev);
}
 if (meta && e.key.toLowerCase() ==='j') {
 e.preventDefault();
 setCopilotOpen((prev) => !prev);
}
 if (e.key ==='Escape') {
 if (paletteOpen) setPaletteOpen(false);
 else if (copilotOpen) setCopilotOpen(false);
}
};
 window.addEventListener('keydown', handleKeyDown);
 return () => window.removeEventListener('keydown', handleKeyDown);
}, [paletteOpen, copilotOpen]);

 // Track active menu id from URL path.
 //
 // The URL pattern is `/{moduleId}/view/{viewId}` (when a view link was
 // clicked) or just `/{moduleId}` (when only the module link was clicked).
 // Previously this hook always set activeMenuId to the module-level item,
 // which meant clicking a view link in the sidebar still highlighted the
 // module name instead of the view (P41 bug report).
 //
 // The fix: when a viewId segment is present, walk the menu tree looking
 // for a child whose `actionId` resolves to that viewId, and set the
 // active id to THAT child. We fall back to the module-level item if no
 // child matches (legacy `/foo` style URLs).
 useEffect(() => {
 const segs = location.pathname.split('/').filter(Boolean);
 const moduleId = segs[0];
 if (!moduleId) {
 setActiveMenuId(undefined);
 return;
}
 // The URL is one of:
 //   /{moduleId}                       — module landing
 // /{moduleId}/{viewSlug}  — clean URL
 //   /{moduleId}/view/{viewId}         — legacy URL
 // For the clean form, resolve the slug back to a full viewId so the
 // child-menu lookup below can find the matching menu item.
 let viewId: string | undefined;
 if (segs[1] === 'view' && segs[2]) {
 viewId = segs[2];
 } else if (segs[1]) {
 const moduleViews = viewRegistry.listViews()
 .filter((v) => v.moduleId === moduleId)
 .map((v) => v.viewId);
 viewId = resolveSlugToViewId(segs[1], moduleViews);
 }

 const all = menuRegistry.getTree();
 const match = all.find((n) => n.moduleId === moduleId);
 if (!match) return;
 const moduleItem = match.item;

 // Helper: walk the tree returning the first node whose actionId points
 // at the given viewId. Menu items have an `actionId` referencing
 // actionRegistry; we resolve each actionId to its view here.
 const findChildForView = (
 nodes: MenuTreeNode[] | undefined,
 targetViewId: string,
): UiMenuItem | undefined => {
 if (!Array.isArray(nodes)) return undefined;
 for (const n of nodes) {
 const item = n.item;
 if (item?.actionId) {
 const action = actionRegistry.getAction(item.actionId);
 if (action?.viewId === targetViewId) return item;
}
 const found = findChildForView(n.children, targetViewId);
 if (found) return found;
}
 return undefined;
};

 if (viewId) {
 // Search the matched module's subtree for a menu item bound to the
 // current viewId. If we find one, highlight IT — not the module.
 const childItem = findChildForView(match.children, viewId);
 if (childItem) {
 setActiveMenuId(childItem.menuId ?? childItem.id);
 return;
}
}

 // Fallback: legacy / module-only URLs highlight the module entry.
 setActiveMenuId(moduleItem.menuId ?? moduleItem.id);
}, [location.pathname]);

 // Roles come from the decoded JWT (useCurrentUser), not the shell-facing
 // useAuth().user object which only carries name/email. Feeds RBAC nav
 // visibility and the entitlement evaluator below.
 const userRoles = useAuthRoles();
 const menuTree = menuRegistry.getVisibleMenus(userRoles);

 // Live federation health — degraded modules get a red badge in the nav and
 // a recovery banner with a per-module retry.
 const degradedRemotes = useDegradedRemotes();
 const [dismissedRemotes, setDismissedRemotes] = useState<Set<string>>(new Set());
 const degradedByModuleId = useMemo(() => {
 const map = new Map<string, RemoteHealth>();
 for (const h of degradedRemotes) {
 if (h.moduleId) map.set(h.moduleId, h);
 }
 return map;
 }, [degradedRemotes]);
 const visibleDegraded = useMemo(
 () => degradedRemotes.filter((h) => !dismissedRemotes.has(h.federatedName)),
 [degradedRemotes, dismissedRemotes]
 );

 const sidebarItems = useMemo(
 () => toSidebarItems(menuTree, degradedByModuleId),
 [menuTree, degradedByModuleId]
 );
 const { mode: navMode } = useNavMode();
 // THE gate. Everything below — sidebar, palette, browse catalogue, module
 // host, widgets — consumes this and nothing else. Assembly + batch evaluation
 // live in useShellModuleAccess; the decision logic is in entitlementGate.ts.
 const { access: moduleAccess, retry: retryEntitlements } = useShellModuleAccess({
   roleCount: userRoles.length,
   registryRevision: menuTree,
 });

 // The permitted, mode-filtered sidebar sections. This is the AUTHORITATIVE
 // input to the adaptive layer: it has already passed the role gate
 // (menuRegistry.getVisibleMenus) and the entitlement gate (a real Set — never
 // null — so an unresolved or failed evaluation renders NO nav rather than
 // every module). The adaptive reorder below is a pure permutation of exactly
 // this set.
 const permittedSections = useMemo(
 () =>
   bucketIntoSections(menuTree, sidebarItems, {
     mode: navMode,
     visibleModules: new Set(moduleAccess.permittedModuleIds),
   }),
 [menuTree, sidebarItems, navMode, moduleAccess]
 );

 // The permitted module ids — derived from the already-filtered sections, so it
 // can only ever contain modules the principal is entitled to. Passed to the
 // adaptive facade purely to keep the published copilot scope bounded to this
 // set (adaptation reorders emphasis, never widens entitlement).
 const permittedModuleIds = useMemo(() => {
   const ids = new Set<string>();
   for (const section of permittedSections) {
     for (const item of section.items ?? []) ids.add(moduleKeyForMenuItem(item));
   }
   return [...ids];
 }, [permittedSections]);

 const adaptive = useAdaptiveShell(permittedModuleIds);

 // Adaptive nav ordering: reorder items WITHIN each permitted section by the
 // persona's real (locally-tracked) usage frequency. Deprioritized ≠ removed —
 // every permitted item stays present and reachable; only the order changes.
 // When adaptation is inactive (no signal / opted out) this returns the
 // permitted sections unchanged (honest default ordering).
 const sidebarSections = useMemo(
 () => adaptSidebarSections(permittedSections, adaptive.counts, adaptive.active),
 [permittedSections, adaptive.counts, adaptive.active]
 );

 // Nav filter — applied AFTER permission gating and adaptive ordering, so it
 // can only ever narrow what the operator was already entitled to see.
 const visibleSections = useMemo(
 () => filterSidebarSections(sidebarSections, navQuery),
 [sidebarSections, navQuery]
 );
 const navFilterActive = navQuery.trim() !== '';

 // Shared nav filter box — rendered in the Sidebar's subheader slot (desktop
 // rail + mobile slide-over). Plain search input; Escape clears the query.
 const navFilterBox = (
 <div className="relative">
 <Search
 aria-hidden="true"
 className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
 />
 <Input
 type="search"
 value={navQuery}
 onChange={(e) => setNavQuery(e.target.value)}
 onKeyDown={(e) => {
 if (e.key === 'Escape' && navQuery) {
 e.stopPropagation();
 setNavQuery('');
 }
 }}
 placeholder="Filter navigation…"
 aria-label="Filter navigation"
 disabled={moduleAccess.state !== 'ready'}
 className="h-8 pl-8 text-sm"
 />
 {/* Explicit entitlement states. While the answer is missing the nav is
 EMPTY, not full — and a failed evaluation says so instead of quietly
 rendering the whole catalogue. */}
 {moduleAccess.state === 'loading' && (
 <p role="status" data-testid="nav-entitlements-loading" className="mt-1.5 px-1 text-xs text-muted-foreground">
 Checking your access…
 </p>
 )}
 {moduleAccess.state === 'unavailable' && (
 <div role="alert" data-testid="nav-entitlements-unavailable" className="mt-1.5 px-1">
 <p className="text-xs text-alarm-critical">
 Your access could not be verified, so no modules are shown.
 </p>
 <Button
 type="button"
 variant="outline"
 onClick={retryEntitlements}
 className="h-auto whitespace-normal [&_svg]:size-3 bg-transparent mt-1 inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-[11px] font-medium text-foreground transition-colors hover:bg-accent"
 >
 <RefreshCw className="h-3 w-3" />
 Try again
 </Button>
 </div>
 )}
 {moduleAccess.state === 'ready' && navFilterActive && visibleSections.length === 0 && (
 <p role="status" className="mt-1.5 px-1 text-xs text-muted-foreground">
 No modules or views match “{navQuery.trim()}”.
 </p>
 )}
 </div>
 );

 const handleMenuItemClick = (item: MenuItem) => {
 setActiveMenuId(item.id);
 // Record this open as the honest usage signal that drives adaptive ordering.
 // Recording is downstream of entitlement: an item can only be clicked if it
 // was rendered, and it is only rendered if it survived the permission gates.
 adaptive.recordUse(moduleKeyForMenuItem(item));

 const menuItem = menuRegistry.getItem(item.id);
 if (!menuItem) return;

 const moduleId: string | undefined = menuItem.moduleId;
 if (!moduleId) return;

 if (menuItem.actionId) {
 const action = actionRegistry.getAction(menuItem.actionId);
 if (action?.viewId) {
 const allViews = viewRegistry.listViews();
 const targetView = allViews.find((v) => v.viewId === action.viewId);
 if (targetView) {
 // Clean URL form: /<moduleId>/<slug>
 navigate(`/${moduleId}/${viewSlugForUrl(targetView.viewId)}`);
 return;
}
}
}

 const views = viewRegistry.listViews().filter((v) => v.moduleId === moduleId);
 const defaultView = views.find((v) => v.isDefault) || views[0];
 if (defaultView?.viewId) {
 navigate(`/${moduleId}/${viewSlugForUrl(defaultView.viewId)}`);
} else {
 navigate(`/${moduleId}`);
}
};

 const handleRetryRemote = (federatedName: string) => {
 void remoteHealth.retry(federatedName);
 };

 // PageHeader (the design-system breadcrumb owner) and some module views
 // render plain `<a href="/…">` links; a raw anchor triggers a FULL page
 // reload, dropping the registries and re-running the whole manifest load.
 // Intercept unmodified left-clicks on same-origin anchors inside the main
 // content area and route them through the SPA router instead. External
 // links, downloads, new-tab targets, hash anchors, non-http schemes and
 // modified clicks (cmd/ctrl/shift/alt, middle button) pass through untouched.
 const handleContentLinkClick = (e: React.MouseEvent<HTMLElement>) => {
 if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
 return;
 }
 const anchor = (e.target as HTMLElement | null)?.closest?.('a[href]');
 if (!(anchor instanceof HTMLAnchorElement)) return;
 if (anchor.target && anchor.target !== '_self') return;
 if (anchor.hasAttribute('download')) return;
 const rawHref = anchor.getAttribute('href') ?? '';
 if (rawHref === '' || rawHref.startsWith('#')) return;
 let url: URL;
 try {
 url = new URL(anchor.href, window.location.href);
 } catch {
 return;
 }
 if (url.origin !== window.location.origin) return;
 if (url.protocol !== 'http:' && url.protocol !== 'https:') return;
 e.preventDefault();
 navigate(url.pathname + url.search + url.hash);
 };

 const moduleLabelForRemote = (h: RemoteHealth): string => {
 if (h.moduleId) {
 const entry = moduleManifestRegistry.get(h.moduleId);
 if (entry?.name) return entry.name as string;
 }
 return h.federatedName.replace(/_ui$/, '').replace(/_/g, ' ');
 };

 const brandingHeader = (
 <Button
 type="button"
 variant="ghost"
 onClick={() => navigate('/')}
 className="h-auto whitespace-normal p-0 hover:bg-transparent flex items-center gap-2.5 min-w-0 group"
 aria-label="Plant Canvas home"
 >
 {/* Tyndo mark — block-style lowercase t with purple → yellow-orange gradient. */}
 <img
 src={tyndoMark}
 alt=""
 aria-hidden="true"
 className="h-8 w-8 shrink-0 object-contain"
 />
 {!sidebarCollapsed && (
 <span className="text-base font-semibold tracking-tight truncate group-hover:text-primary transition-colors">
 Tyndo
 </span>
 )}
 </Button>
 );

 // Bottom-nav shortcuts shown on small breakpoints (< md). A phone/tablet
 // operator gets the top destinations one tap away; anything beyond the pinned
 // slots spills into a "More" drawer so the bar never overflows. The pinned
 // set is persona-driven: when a persona workspace is active we lead with its
 // primary modules; otherwise we fall back to the default operator shortcuts.
 const HOME_NAV: BottomNavItem = { id:'home', label:'Home', href:'/', icon: Home};
 const DEFAULT_BOTTOM_NAV: BottomNavItem[] = [
 HOME_NAV,
 { id:'equipment', label:'Equipment', href:'/tyndo.equipment', icon: Boxes},
 { id:'production', label:'Production', href:'/tyndo.production', icon: Factory},
 { id:'alarms', label:'Alarms', href:'/tyndo.alarms', icon: BellRing},
 { id:'maintenance', label:'Maintenance', href:'/tyndo.maintenance', icon: Wrench},
];

 // Max items shown inline on the bottom bar before the "…More" affordance.
 // Home is always pinned first; the remaining slots are filled from the
 // persona-resolved module set (or the default operator shortcuts).
 const MAX_PINNED = 4;
 const [moreNavOpen, setMoreNavOpen] = useState(false);

 const bottomNavCandidates = useMemo<BottomNavItem[]>(() => {
 // Entitlement-bounded destinations. The hardcoded DEFAULT_BOTTOM_NAV
 // fallback is NOT used as an entitlement escape hatch: it is filtered
 // through the same gate, so an unentitled module can never reach the bar
 // just because the permitted set happened to be empty.
 const permittedNav: BottomNavItem[] = [];
 if (moduleAccess.state === 'ready') {
 const manifestEntries = moduleManifestRegistry.list?.() ?? [];
 for (const m of manifestEntries) {
 const id: string | undefined = m?.moduleId;
 if (!id || !moduleAccess.permittedModuleIds.has(id)) continue;
 const icon = resolveIcon(m?.icon) ?? Circle;
 permittedNav.push({ id, label: m?.name ?? id, href: `/${id}`, icon });
 }
 }
 const defaults = DEFAULT_BOTTOM_NAV.slice(1).filter((item) =>
 moduleAccess.permittedModuleIds.has(item.href.replace(/^\//, '')),
 );
 const tail = permittedNav.length > 0 ? permittedNav : defaults;
 // De-dupe by href, Home always first (Home is the shell's own route, not a
 // module destination, so it is never entitlement-gated).
 const seen = new Set<string>([HOME_NAV.href]);
 const ordered: BottomNavItem[] = [HOME_NAV];
 for (const item of tail) {
 if (seen.has(item.href)) continue;
 seen.add(item.href);
 ordered.push(item);
 }
 return ordered;
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [moduleAccess]);

 const pinnedNavItems = bottomNavCandidates.slice(0, MAX_PINNED);
 const overflowNavItems = bottomNavCandidates.slice(MAX_PINNED);
 const bottomNavItems = pinnedNavItems;

 const handleMobileMenuClick = (item: MenuItem) => {
 handleMenuItemClick(item);
 // Close the slide-over after a navigation so the operator sees the
 // destination view immediately on small screens.
 setMobileNavOpen(false);
};

 return (
 // Every routed surface below (browse catalogue, module host, deep-link
 // redirects, widget canvas) reads the entitlement gate from this provider,
 // so there is exactly ONE permitted-module set in the shell.
 <ModuleAccessProvider access={moduleAccess}>
 <div className="flex h-screen bg-surface-base text-foreground">
 {/* WCAG 2.4.1 — keyboard-first skip link to the main content landmark. */}
 <SkipLink />

 {/* Persistent fixed hamburger — top-left, always present at < md regardless
 of whether the TopBar has rendered yet. Fixed positioning + a high z-index
 (40, below the slide-over at 50) keep it tappable over any module content,
 and its own semi-transparent backdrop gives it contrast on a busy canvas
 before the federated module's CSS has hydrated. This is the resilient
 entry point to navigation: even if the TopBar's own toggle fails to mount,
 the operator can still open the menu. Hidden once the slide-over is open
 (the slide-over carries its own close affordance + backdrop). */}
 {!mobileNavOpen && (
 <Button
 type="button"
 variant="ghost"
 onClick={() => setMobileNavOpen(true)}
 aria-label={t('a11y.openSidebar')}
 aria-expanded={mobileNavOpen}
 className="whitespace-normal p-0 [&_svg]:size-5 glass-strong md:hidden fixed left-[max(0.75rem,env(safe-area-inset-left))] top-[max(0.75rem,env(safe-area-inset-top))] z-40 inline-flex h-10 w-10 items-center justify-center rounded-full border border-border/60 text-foreground shadow-[var(--shadow-md)] hover:bg-accent/60 motion-reduce:transition-none"
 >
 <MenuIcon className="h-5 w-5" aria-hidden="true" />
 </Button>
 )}

 {/* Desktop sidebar — hidden below md; the same component renders inside
 the mobile slide-over below at small breakpoints. */}
 <div className="hidden md:block flex-shrink-0">
 <Sidebar
 menuItems={sidebarItems}
 sections={visibleSections}
 activeMenuId={activeMenuId}
 collapsed={sidebarCollapsed}
 onCollapsedChange={setSidebarCollapsed}
 onMenuClick={handleMenuItemClick}
 header={brandingHeader}
 subheader={navFilterBox}
 collapsedSectionIds={collapsedGroups}
 onSectionToggle={handleSectionToggle}
 autoCollapseChildren
 expandAll={navFilterActive}
 />
 </div>

 {/* Mobile slide-over sidebar — Sheet overlay at < md. The Sheet primitive
 (Radix Dialog) supplies the scrim, focus trap, Escape handling, and
 focus restoration; ≥ md keeps the collapsible column above. */}
 <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
 <SheetContent
 side="left"
 showCloseButton={false}
 className="md:hidden w-auto max-w-[85vw] p-0 pl-[env(safe-area-inset-left)] bg-surface-base"
 >
 <SheetTitle className="sr-only">{t('a11y.navigation')}</SheetTitle>
 <Sidebar
 menuItems={sidebarItems}
 sections={visibleSections}
 activeMenuId={activeMenuId}
 collapsed={false}
 onMenuClick={handleMobileMenuClick}
 header={brandingHeader}
 subheader={navFilterBox}
 collapsedSectionIds={collapsedGroups}
 onSectionToggle={handleSectionToggle}
 autoCollapseChildren
 expandAll={navFilterActive}
 />
 </SheetContent>
 </Sheet>

 <div className="flex flex-col flex-1 overflow-hidden">
 <TopBar
 onOpenPalette={() => setPaletteOpen(true)}
 onToggleMobileNav={() => setMobileNavOpen((v) => !v)}
 mobileNavOpen={mobileNavOpen}
 adaptive={adaptive}
 />
 <SeverityStripe />

 {/* Federation recovery banner — when a module's federated bundle fails
 to load (404/CORS/hung dev server/version skew), surface it explicitly
 with a per-module retry instead of leaving the operator on an
 indefinite "Coming soon". The background scheduler also re-attempts
 automatically; this gives an immediate manual recourse. */}
 {visibleDegraded.length > 0 && (
 <div
 role="alert"
 aria-live="polite"
 data-testid="shell-degraded-banner"
 /* BOUNDED BY CONSTRUCTION. This list is as long as the number of
  * degraded remotes, which is unbounded — on a shop-floor network
  * every module can be degraded at once. The parent column is
  * `overflow-hidden`, so an uncapped stack pushes <main> (and this
  * banner's own Retry/Dismiss buttons) past the bottom of a
  * non-scrolling viewport, where NOTHING can reach them: the
  * document does not scroll and no ancestor is scrollable. On a
  * 1280x720 industrial panel that is a dead screen.
  *
  * `shrink-0` keeps it from being squeezed to nothing, `max-h-[25vh]`
  * keeps it from eating the operator's work area, and `overflow-y-auto`
  * makes the overflow reachable instead of clipped. */
 className="shrink-0 max-h-[25vh] overflow-y-auto border-b border-alarm-critical/35 bg-alarm-critical/[0.06] px-4 py-2"
 /* No `tabIndex` here on purpose. A scrollable region must be reachable by
  * keyboard (WCAG 2.1.1, axe `scrollable-region-focusable`), and this one
  * always is BY ITS CONTENT: every row carries a Dismiss button, and Retry
  * when the remote allows it, so tabbing walks the list and scrolls it. Adding
  * a tab stop to the container as well would put a focusable non-interactive
  * element in the order for no gain. */
 aria-label={t('shell.degradedModules', { defaultValue: 'Degraded modules' })}
 >
 {visibleDegraded.length > 1 && (
 <div className="mx-auto mb-1.5 flex max-w-7xl justify-end">
 <Button
 type="button"
 variant="outline"
 onClick={() =>
 setDismissedRemotes((prev) => {
 const next = new Set(prev);
 for (const h of visibleDegraded) next.add(h.federatedName);
 return next;
 })
 }
 className="h-auto whitespace-normal rounded-md border border-border bg-surface-raised px-2 py-1 text-xs font-medium text-foreground transition-colors hover:bg-accent"
 >
 {t('shell.dismissAllDegraded', {
 defaultValue: 'Dismiss all {{count}} warnings',
 count: visibleDegraded.length,
 })}
 </Button>
 </div>
 )}
 <ul className="mx-auto flex max-w-7xl flex-col gap-1.5">
 {visibleDegraded.map((h) => (
 <li
 key={h.federatedName}
 className="flex flex-wrap items-center gap-2 text-sm"
 >
 <AlertTriangle className="h-4 w-4 shrink-0 text-alarm-critical" />
 <span className="font-medium text-foreground">
 {moduleLabelForRemote(h)}
 </span>
 <span className="text-muted-foreground">
 — {REMOTE_STATUS_LABEL[h.status] ?? 'Module degraded'}
 {h.attempts > 0 ? ` (attempt ${h.attempts})` : ''}
 </span>
 <span className="ml-auto flex items-center gap-1">
 {remoteHealth.canRetry(h.federatedName) && (
 <Button
 type="button"
 variant="outline"
 onClick={() => handleRetryRemote(h.federatedName)}
 disabled={h.retrying}
 className="h-auto whitespace-normal [&_svg]:size-3.5 inline-flex items-center gap-1 rounded-md border border-border bg-surface-raised px-2 py-1 text-xs font-medium text-foreground transition-colors hover:bg-accent disabled:cursor-not-allowed disabled:opacity-60"
 >
 {h.retrying ? (
 <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" />
 ) : (
 <RefreshCw className="h-3.5 w-3.5" />
 )}
 {h.retrying ? 'Retrying…' : 'Retry'}
 </Button>
 )}
 <Button
 type="button"
 variant="ghost"
 onClick={() =>
 setDismissedRemotes((prev) => {
 const next = new Set(prev);
 next.add(h.federatedName);
 return next;
 })
 }
 aria-label={`Dismiss ${moduleLabelForRemote(h)} warning`}
 className="h-auto w-auto whitespace-normal [&_svg]:size-3.5 rounded-md p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
 >
 <XIcon className="h-3.5 w-3.5" />
 </Button>
 </span>
 </li>
 ))}
 </ul>
 </div>
 )}

 {/* Delegated same-origin link interception. No keyboard listener is
 needed: the handler only re-routes clicks on <a> descendants, and
 keyboard activation of a focused anchor (Enter) dispatches a click
 event that this same delegate receives — keyboard users get identical
 SPA behaviour, nothing is click-only. */}
 {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */}
 <main
 id="main"
 role="main"
 aria-label="Main content"
 onClick={handleContentLinkClick}
 /* `min-h-0` is load-bearing, not cosmetic: a flex item's default
  * `min-height:auto` refuses to shrink below its content, so without it
  * a tall module view makes <main> overflow the `overflow-hidden`
  * column instead of scrolling inside its own `overflow-auto` box —
  * and the clipped remainder is unreachable at every viewport. */
 className="flex-1 min-h-0 overflow-auto bg-surface-base md:rounded-tl-2xl md:border-l md:border-t md:border-border/50 pb-[calc(5.5rem+env(safe-area-inset-bottom))] md:pb-0"
 >
 <Outlet />
 </main>

 <MobileBottomNav
 pinned={bottomNavItems}
 overflow={overflowNavItems}
 moreOpen={moreNavOpen}
 onMoreOpenChange={setMoreNavOpen}
 />

 <CommandPalette
 open={paletteOpen}
 onOpenChange={setPaletteOpen}
 moduleAccess={moduleAccess}
 />

 {/* Global toast surface — shell components (ModuleRedirect timeout,
 ui-kit domain flows) call `toast()` from @tyndo/ui-kit; without a
 mounted Toaster those calls would silently no-op. */}
 <Toaster />

 <Suspense fallback={null}>
 <CopilotPanel
 isOpen={copilotOpen}
 onToggle={(open: boolean) => setCopilotOpen(open)}
 />
 </Suspense>

 {/* Ambient Copilot Rail (F0-9) — persistent, module-context-scoped rail,
 docked on wide screens so it is woven into every view without shoving
 the main content. Scoped to the active module from the route; the rail
 self-gates on permission and stays quiet when nothing is grounded. Only
 rendered on a module route (not the bare landing page). */}
 {location.pathname.split('/').filter(Boolean).at(0) ? (
 <Suspense fallback={null}>
 {/* The positioning box is a fixed 20rem column, but the rail inside it
 is collapsed by default and renders NOTHING at all when the
 principal lacks `copilot:read` or there is nothing grounded.
 Without `pointer-events-none` that empty column silently swallowed
 every click landing in the right ~20rem of the content area — a
 toolbar button, a list row action, a dialog's confirm — with no
 visual indication anything was over them. Pointer events are
 re-enabled on the rail's own box, which is only as tall as the rail
 actually draws, so the empty space belongs to the content again.

 ANCHORED TO THE BOTTOM, not the top. `pointer-events-none` fixed
 the empty column; it did nothing about the card the rail DOES
 draw. Measured at 1280×720 on a module route, the collapsed card
 occupies x 930–1260, y 99–157 — and every console's
 ConsoleArchetypeBar puts its action cluster (Retry, Refresh,
 overflow) at x≈1160, y≈112–144. The card sat squarely on top of
 them: clicks went to the rail's own "Show" button instead, so the
 toolbar's Retry was unreachable for as long as the rail was
 mounted. It presented as flakiness, because whether the lazy rail
 chunk had resolved decided whether the click landed.

 Height is now content-sized and pinned to the BOTTOM of the
 gutter, clear of the band every console reserves for its controls.
 `max-h` keeps the old ceiling, so an EXPANDED rail — which the
 operator opened deliberately — grows into the same envelope as
 before and never higher.

 The offset was `bottom-28` to sit above the floating Copilot
 button. That button is gone (UX-A5), so the rail drops to the same
 `bottom-6` inset everything else uses and the 5.5rem it was
 holding open for a control that no longer exists goes back to the
 page. */}
 <div className="pointer-events-none hidden xl:block fixed right-4 bottom-6 z-30 max-h-[calc(100vh-13rem)] w-80 max-w-[22rem] overflow-y-auto">
 <div className="pointer-events-auto">
 <AmbientCopilotRail
 moduleId={location.pathname.split('/').filter(Boolean).at(0) as string}
 defaultCollapsed
 />
 </div>
 </div>
 </Suspense>
 ) : null}

 {/* The floating Copilot button used to live here (docs/latest-analysis-ui-ux.md
 UX-A5). A live session at 1280×720 found three Copilot entry points on one
 screen: the top bar's control, this FAB, and the ambient rail's own
 Show/Refresh card. The FAB was the one to lose — it duplicated the top-bar
 control exactly, and unlike the rail it overlaid page content at every
 width rather than only where there was gutter for it.

 The top-bar trigger now shows from `sm` up (icon-only until `lg`) and the
 kebab carries it below that, so removing this took no width's only way in.
 See TopBar.tsx. */}
 </div>
 </div>
 </ModuleAccessProvider>
 );
}
